import React, { useState } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import { Box, Button, TextField, Card, CardContent, CardHeader, Divider, Backdrop, CircularProgress, Grid } from '@material-ui/core';
import { useRequestService } from '../../services/RequestService';
import { Resource, ResourceType } from './types';
import { useLocation, useNavigate } from 'react-router';
import { AlertMessage } from '../../components/alert';
import { Autocomplete } from '@material-ui/lab';
import { resourceTypeMapper } from '../../services/resourceTemplates';
import useAsync from 'react-use/esm/useAsync';
import { AutocompleteInput } from '../../components/input';

export const NewResourcePage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const requestService = useRequestService();
    const { value: systems = []} = useAsync(async () => await requestService.listSystems(), []);

    const [formValues, setFormValues] = useState<Resource>({
        system: state?.owner || '',
        environment: ['dev', 'stg', 'prd'],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const resourceTypes = Object.entries(ResourceType)
        .map(([key, value]) => ({
            label: value as string,
            value: key,
        }));

    const handleChange = (field: string, value: any) => {
        setFormValues({ ...formValues, [field]: value    });
    };

    const currentFields = resourceTypeMapper[formValues.type as keyof typeof ResourceType]?.fields ?? [];

    const isFormValid =
        !!formValues.type &&
        currentFields.every(field => {
            const value = formValues[field.name];
            if (field.name === 'environment') {
                // Check if the environment field is an array and has at least one non-empty string
                return Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string' && v.trim() !== '');
            }
            return typeof value === 'string' && value.trim() !== '';
        });

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await requestService.createResource(formValues);
            navigate('/requests', { state: { message: 'Resource request created successfully!' } });
        } catch (err) {
            setError('Failed to submit resource request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Page themeId="home">
            <Header title="New Resource" type="New request" typeLink='/requests/new-request' />
            <Content>
                <Card>
                    <CardHeader title="Create a new resource" subheader="Fill out the form below to create a new resource request. Once submitted, you will be able to track the status of your request on the requests page." />
                    <CardContent>
                        <Divider />

                        <Box
                            component="form"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{ gap: '16px' }}
                            mt={3}
                        >
                            <Box width="100%" maxWidth="800px">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <AutocompleteInput
                                            label="Type"
                                            fieldName="type"
                                            formValues={formValues}
                                            options={resourceTypes}
                                            onChange={handleChange}
                                        />
                                    </Grid>

                                    {resourceTypeMapper[formValues.type as keyof typeof ResourceType]?.fields?.map(field => {
                                    if (field.name === 'system') {
                                        return (
                                        <Grid item xs={12} md={6} key={field.name}>
                                            <AutocompleteInput
                                                label={field.label}
                                                fieldName={field.name}
                                                formValues={formValues}
                                                options={systems.map(s => ({ value: String(s.value), label: s.label }))}
                                                onChange={handleChange}
                                                disabled={!!state?.owner}
                                            />
                                        </Grid>
                                        );
                                    }
                                    if (field.name === 'environment' && field.options) {
                                    return (
                                        <Grid item xs={12} md={12} key={field.name}>
                                        <Autocomplete
                                            multiple
                                            options={field.options}
                                            getOptionLabel={(option) => option.label}
                                            value={field.options.filter(opt =>
                                            (formValues[field.name] || []).toLocaleString().includes(opt.value)
                                            )}
                                            onChange={(_, selected) =>
                                            setFormValues(prev => ({
                                                ...prev,
                                                [field.name]: selected.map(opt => opt.value),
                                            }))
                                            }
                                            renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={field.label}
                                                variant="outlined"
                                                required
                                                fullWidth
                                            />
                                            )}
                                        />
                                        </Grid>
                                    );
                                    }

                                    return (
                                        <Grid item xs={12} md={field.multiline ? 12 : 6} key={field.name}>
                                        <TextField
                                            label={field.label}
                                            variant="outlined"
                                            value={formValues[field.name] || ''}
                                            onChange={event => handleChange(field.name, event.target.value)}
                                            required
                                            fullWidth
                                            multiline={field.multiline}
                                            minRows={field.multiline ? 4 : undefined}
                                        />
                                        </Grid>
                                    );
                                    })}

                                </Grid>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="flex-end" mt={3}>
                            <Button onClick={handleSubmit} disabled={!isFormValid} variant="contained" color="primary">
                                Submit
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Content>

            <Backdrop open={loading} style={{ zIndex: 1300 }}>
                <CircularProgress color="primary" />
            </Backdrop>

            {error && <AlertMessage message={error} />}
        </Page>
    );
};
